import { Box, Typography, Divider, Tooltip, IconButton, Tabs, Tab } from "@mui/material";
import { Close } from "@mui/icons-material";
import type { RootState } from "../store";
import { useSelector, useDispatch } from "react-redux";
import { useCallback, useState } from "react";
import LibraryAddIcon from "@mui/icons-material/LibraryAdd";
import {
    addImageRecipe,
    addNameRecipe,
    addRecipe,
    clearImageRecipes,
    clearNameRecipes,
    clearRecipes,
    removeImageRecipe,
    removeNameRecipe,
    removeRecipe,
    ungeneratePotentialImageRecipes,
} from "../store/slices/recipes";
import Recipe from "./Recipe";
import type { IRecipe } from "../types/recipe";
import ChapterAddDialog from "./ChapterAddDialog";

interface IGenerationRecipeProps {
    type: "parameters" | "image" | "name";
    recentRecipes: IRecipe[];
    startingTab?: number;
}

const DISPATCH_FUNCTIONS = {
    name: {
        addRecipe: addNameRecipe,
        clearRecipes: clearNameRecipes,
        removeRecipe: removeNameRecipe,
    },
    parameters: {
        addRecipe: addRecipe,
        clearRecipes: clearRecipes,
        removeRecipe: removeRecipe,
    },
    image: {
        addRecipe: addImageRecipe,
        clearRecipes: clearImageRecipes,
        removeRecipe: removeImageRecipe,
    },
};

const GenerationRecipe: React.FC<IGenerationRecipeProps> = ({ type, startingTab = 0, recentRecipes }) => {
    const [tab, setTab] = useState(startingTab);
    const [openModal, setOpenModal] = useState(false);

    const authState = useSelector((state: RootState) => state.auth);
    const isGuest = !authState.account;

    const dispatch = useDispatch();

    const handleChange = useCallback((_: React.SyntheticEvent, newValue: number) => {
        setTab(newValue);
    }, []);

    const handleDelete = useCallback(
        (index: number) => {
            if (type === "image") {
                dispatch(ungeneratePotentialImageRecipes({ generationId: recentRecipes[index].generationId }));
            }

            dispatch(DISPATCH_FUNCTIONS[type].removeRecipe(String(index)));

            if (index + 1 === recentRecipes.length) {
                setTab(index - 1);
            }
        },
        [dispatch, recentRecipes, type]
    );

    if (!recentRecipes?.length) return null;

    return (
        <Box sx={{ height: "100%", width: "100%" }}>
            <Box sx={{ height: "calc(100dvh - 109px)", display: "flex", flexDirection: "column", maxWidth: "100%" }}>
                <Box sx={{ display: "flex", justifyContent: "center", maxWidth: "100%" }}>
                    <Tabs
                        sx={{ width: "calc(100vw - 92px)" }}
                        value={tab}
                        onChange={handleChange}
                        variant="scrollable"
                        scrollButtons
                        allowScrollButtonsMobile
                    >
                        {recentRecipes.map((recipe, index) => (
                            <Tab
                                key={recipe.name + index}
                                label={
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                width: "100px",
                                                mr: 1,
                                            }}
                                        >
                                            {recipe.name}
                                        </Typography>
                                        <Box
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDelete(index);
                                            }}
                                        >
                                            <Close fontSize="small" />
                                        </Box>
                                    </Box>
                                }
                            />
                        ))}
                    </Tabs>
                </Box>

                {!isGuest && (
                    <Box sx={{ display: "flex", height: "32px", justifyContent: "right", marginBottom: "4px" }}>
                        <Tooltip title="Save">
                            <IconButton onClick={() => setOpenModal(true)}>
                                <LibraryAddIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}

                <Divider />

                <Recipe recipe={recentRecipes[tab] as IRecipe} />
            </Box>

            {openModal && <ChapterAddDialog setOpenModal={setOpenModal} recipe={recentRecipes[tab]} saveable={true} />}
        </Box>
    );
};

export default GenerationRecipe;
